import {
  suggestDecision,
  inReview,
  REVIEW_FILTERS,
  REVIEW_FILTER_LABEL,
  REVIEW_STATE_LABEL,
  matchesFilter,
  type ChangeRequest,
  type ReviewFilter,
  type ReviewRow,
} from '@cyberscore/contracts';
import {
  AlertTriangle,
  Check,
  ChevronRight,
  FileSearch,
  Flag,
  Gauge,
  MessageSquare,
  MessageSquareReply,
  RotateCcw,
  Send,
  Undo2,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { hashQuery, href, navigate, useHash } from '../../lib/router';
import { useToast } from '../../ui/Toasts';
import { useAssessment, useQuestions } from '../assessments/api';
import { STATUS_LABEL } from '../assessments/labels';
import { summarize, type QuestionRow } from '../assessments/flow';
import {
  useRequestChange,
  useReview,
  useReviewAccess,
  useReviewActions,
  useSendToBusiness,
  useStartReview,
  useWithdrawRequest,
} from './api';
import { ClaimList } from '../claims/ClaimList';
import { useScore } from '../portfolio/api';
import { BelowMinimum } from '../portfolio/ScorePage';
import { useRunClaims } from '../claims/api';
import { Conditions } from '../decisions/Conditions';
import { MessagesLink } from '../messages/MessagesLink';
import { DecidePanel } from '../decisions/DecidePanel';
import { useInsurerForms } from '../insurers/api';
import { FollowUpsCard } from '../follow-ups/FollowUpsCard';
import { bulkApprovable, exceptionsFirst, filterCounts, PROVENANCE_LABEL, STATE_TONE } from './review';

/**
 * The underwriter's review of a submitted assessment (task 61, decision 0037): every answer with where
 * it stands, filtered to what needs a look, failing answers first in mind. Approve, flag for the insurer's
 * own attention, or ask the business for a change; changes go to the business together.
 */
export function ReviewPage({ id }: { id: string }) {
  const detail = useAssessment(id);
  const review = useReview(id);
  const qs = useQuestions(id);
  const score = useScore(id);
  const forms = useInsurerForms(id);
  const access = useReviewAccess();
  const hash = useHash();
  const params = hashQuery(hash);
  const filter = (REVIEW_FILTERS as readonly string[]).includes(params.get('filter') ?? '') ? (params.get('filter') as ReviewFilter) : 'needs_review'; // prettier-ignore
  const stage = params.get('stage');
  const setParams = (next: { filter?: ReviewFilter; stage?: string | null }) => {
    const p = new URLSearchParams();
    const f = next.filter ?? filter;
    const s = next.stage === undefined ? stage : next.stage;
    if (f !== 'needs_review') p.set('filter', f);
    if (s) p.set('stage', s);
    navigate(`${href.review(id)}${p.size ? `?${p}` : ''}`, { replace: true });
  };

  const defs = useMemo(() => new Map((qs.data?.questions ?? []).map((q) => [q.key, q])), [qs.data]);

  if (detail.isPending || review.isPending) return <div className="empty">Loading…</div>;
  if (detail.isError || review.isError)
    return (
      <div className="empty">
        <strong>Could not load the review</strong>
        {(detail.error ?? review.error)?.message}
      </div>
    );

  const a = detail.data;
  const v = review.data;
  const inStage = v.rows.filter((r) => !stage || r.stage?.key === stage);
  const counts = filterCounts(inStage);
  const shown = exceptionsFirst(inStage.filter((r) => matchesFilter(filter, r)));
  const p = v.progress;
  const done = p.total ? p.reviewed / p.total : 0;

  return (
    <>
      <div className="crumbs">
        <a href={href.clients()}>Clients</a>
        <ChevronRight size={14} />
        <a href={href.assessment(id)}>{a.customer_name}</a>
        <ChevronRight size={14} />
        Review
      </div>
      <div className="page-head row-between">
        <div>
          <h1>Review: {a.customer_name}</h1>
          <p>
            {a.title} · <span className={`pill ${a.status === 'in_remediation' ? 'warn' : 'neutral'}`}>{STATUS_LABEL[a.status]}</span>
            {a.submitted_at && ` · submitted ${new Date(a.submitted_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}`}
          </p>
        </div>
        <div className="row gap">
          <a className="btn" href={href.score(id)}>
            <Gauge size={16} /> CyberScore
          </a>
          <MessagesLink id={id} />
          {access.write && a.status !== 'closed' && <RunChecks id={id} />}
          {access.write && <StatusActions id={id} status={a.status} unsent={v.unsent} />}
        </div>
      </div>

      {a.status === 'in_progress' && (
        <div className="notice neutral">The business is still answering. You can review once they submit.</div>
      )}
      {score.data && <BelowMinimum r={score.data.score} />}
      {a.status === 'in_remediation' && (
        <div className="notice" role="status">
          With the business for {pluralChanges(v.rows.filter((r) => r.requests.some((q) => q.state === 'sent')).length)}.
          Changes you ask for now go to them straight away.
        </div>
      )}

      <div className="layout-side">
        <div className="stack">
          {a.status !== 'in_progress' && <Conditions id={id} mode="staff" />}
          {a.status !== 'in_progress' && <FollowUpsCard id={id} canWrite={access.write && a.status !== 'closed'} />}
          <section className="card pad">
            <div className="row-between">
              <div className="eyebrow">Review progress</div>
              <span className="muted small num">{p.reviewed} of {p.total} reviewed</span>
            </div>
            <span className="meter mt" aria-label={`${Math.round(done * 100)}% reviewed`}>
              <span className={`fill ${done === 1 ? 'ok' : ''}`} style={{ width: `${done * 100}%` }} />
            </span>
            <div className="review-counts">
              <span><b>{p.to_review}</b> to review</span>
              <span><b>{p.changed}</b> changed</span>
              <span><b>{p.approved}</b> approved</span>
              <span><b>{p.flagged}</b> flagged</span>
              <span><b>{p.change_requested}</b> change requested</span>
            </div>
          </section>

          {access.write && inReview(a.status) && <BulkApprove id={id} rows={shown} />}

          <div className="row gap wrap" role="group" aria-label="Show">
            {REVIEW_FILTERS.map((f) => (
              <button key={f} type="button" className={`chip ${f === filter ? 'on' : ''}`} aria-pressed={f === filter} onClick={() => setParams({ filter: f })}>
                {REVIEW_FILTER_LABEL[f]} <span className="muted num">{counts[f]}</span>
              </button>
            ))}
          </div>

          {shown.length === 0 ? (
            <section className="card pad">
              <p className="muted small">
                {filter === 'needs_review' ? 'Nothing left to review here.' : `No answers ${REVIEW_FILTER_LABEL[filter].toLowerCase()} here.`}
              </p>
            </section>
          ) : (
            <ol className="review-list">
              {shown.map((r) => (
                <ReviewItem key={r.key} id={id} row={r} def={defs.get(r.key)} canWrite={access.write && inReview(a.status)} />
              ))}
            </ol>
          )}
        </div>

        <aside className="stack">
          {access.write && inReview(a.status) && (
            <DecidePanel
              id={id}
              suggestion={
                score.data
                  ? suggestDecision({
                      score: score.data.score,
                      forms: (forms.data ?? []).filter((f) => f.chosen).map((f) => ({ insurer: f.insurer, product: f.product, blockers: f.readiness.blockers })), // prettier-ignore
                    })
                  : null
              }
              unreviewed={p.total - p.reviewed}
              blocked={
                a.status === 'in_remediation'
                  ? 'The business has this assessment for changes. Decide once it is back.'
                  : v.unsent > 0
                    ? `Send or withdraw the ${pluralChanges(v.unsent)} you asked for first.`
                    : null
              }
            />
          )}
          <section className="card pad">
            <div className="eyebrow">Stages</div>
            <ul className="stage-progress">
              <li>
                <button type="button" className={`link-row ${!stage ? 'on' : ''}`} onClick={() => setParams({ stage: null })}>
                  <span className="grow">All stages</span>
                  <span className="muted small num">{p.reviewed}/{p.total}</span>
                </button>
              </li>
              {v.stages.map((s) => (
                <li key={s.key}>
                  <button type="button" className={`link-row ${stage === s.key ? 'on' : ''}`} onClick={() => setParams({ stage: s.key })}>
                    <span className="grow">{s.name}</span>
                    {s.change_requested > 0 && <span className="pill warn">{s.change_requested}</span>}
                    <span className="meter thin"><span className={`fill ${s.reviewed === s.total ? 'ok' : ''}`} style={{ width: `${s.total ? (s.reviewed / s.total) * 100 : 0}%` }} /></span>
                    <span className="muted small num">{s.reviewed}/{s.total}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
          <section className="card pad">
            <div className="eyebrow">How review works</div>
            <p className="muted small">
              Approve answers you accept. Flag one to look at again yourselves; the business doesn’t see flags.
              Ask for a change and it waits here until you send all your requests to the business together. They
              change those answers, or reply to say why one stands, and send it back.
            </p>
          </section>
        </aside>
      </div>
    </>
  ); // prettier-ignore
}

/** Checks every answer against the business's documents again (they also run when answers or documents change). */
function RunChecks({ id }: { id: string }) {
  const run = useRunClaims(id);
  const toast = useToast();
  return (
    <button
      type="button"
      className="btn"
      disabled={run.isPending}
      onClick={async () => {
        try {
          const r = await run.mutateAsync();
          const failing = Object.values(r.summary).filter((v) => v === 'contradicted' || v === 'not_supported').length; // prettier-ignore
          if (!r.checked && !r.failed) toast('No answers have document checks yet', 'ok');
          else if (r.failed) toast(`${r.failed} ${r.failed === 1 ? 'check' : 'checks'} couldn’t run now; try again later`, 'bad');
          else toast(failing ? `Checked: ${failing} ${failing === 1 ? 'answer isn’t' : 'answers aren’t'} backed by the documents` : 'Checked: the documents back every checked answer', 'ok'); // prettier-ignore
        } catch (err) {
          toast(err instanceof Error ? err.message : 'Could not check the documents', 'bad');
        }
      }}
    >
      <FileSearch size={16} /> {run.isPending ? 'Checking…' : 'Check documents'}
    </button>
  );
}

const pluralChanges = (n: number) => `${n} ${n === 1 ? 'change' : 'changes'}`;

function StatusActions({ id, status, unsent }: { id: string; status: string; unsent: number }) {
  const start = useStartReview(id);
  const send = useSendToBusiness(id);
  const toast = useToast();
  const run = async (m: { mutateAsync: () => Promise<unknown> }, ok: string) => {
    try {
      await m.mutateAsync();
      toast(ok, 'ok');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That didn’t work', 'bad');
    }
  };
  return (
    <>
      {status === 'submitted' && (
        <button
          type="button"
          className="btn"
          disabled={start.isPending}
          onClick={() => run(start, 'Review started')}
        >
          Start review
        </button>
      )}
      {unsent > 0 && status !== 'in_remediation' && (
        <button
          type="button"
          className="btn btn-primary"
          disabled={send.isPending}
          onClick={() => run(send, 'Sent to the business')}
        >
          <Send size={15} /> Send {pluralChanges(unsent)} to the business
        </button>
      )}
    </>
  );
}

/**
 * The review's first step (task 110, decision 0074): approve every answer shown that fails no control or
 * document check, in one go, so the reviewer's attention goes to the rest, which are listed first.
 */
function BulkApprove({ id, rows }: { id: string; rows: ReviewRow[] }) {
  const act = useReviewActions(id);
  const toast = useToast();
  const ok = bulkApprovable(rows);
  if (ok.length < 2) return null;
  const rest = rows.filter(
    (r) => (r.state === 'to_review' || r.state === 'changed') && !ok.includes(r),
  ).length;
  return (
    <div className="row-between wrap gap bulk-bar">
      <span className="small">
        <b>{ok.length} answers pass every check.</b>{' '}
        <span className="muted">
          Approve them in one step
          {rest ? `, then look at the ${rest} that need a closer look (listed first)` : ''}.
        </span>
      </span>
      <button
        type="button"
        className="btn btn-primary"
        disabled={act.isPending}
        onClick={async () => {
          try {
            await act.mutateAsync({
              items: ok.map((r) => ({ question: r.key, action: 'approve', based_on: r.answer!.response_id })),
            });
            toast(`Approved ${ok.length} answers`, 'ok');
          } catch (err) {
            toast(err instanceof ApiError && err.status === 409 ? 'Some answers changed; the list has been reloaded' : err instanceof Error ? err.message : 'Could not approve', 'bad'); // prettier-ignore
          }
        }}
      >
        <Check size={14} /> Approve these {ok.length}
      </button>
    </div>
  );
}

type Composer = null | 'flag' | 'request';

function ReviewItem({
  id,
  row,
  def,
  canWrite,
}: {
  id: string;
  row: ReviewRow;
  def?: QuestionRow;
  canWrite: boolean;
}) {
  const act = useReviewActions(id);
  const request = useRequestChange(id);
  const withdraw = useWithdrawRequest(id);
  const toast = useToast();
  const [composer, setComposer] = useState<Composer>(null);
  const [text, setText] = useState('');
  const live = row.requests.find((q) => q.state === 'open' || q.state === 'sent');
  const past = row.requests.filter((q) => q.state === 'answered');
  const busy = act.isPending || request.isPending || withdraw.isPending;

  const fail = (err: unknown) =>
    toast(err instanceof ApiError && err.status === 409 ? 'This answer changed; it has been reloaded' : err instanceof Error ? err.message : 'That didn’t work', 'bad'); // prettier-ignore
  const decide = async (action: 'approve' | 'flag' | 'clear', note?: string) => {
    try {
      await act.mutateAsync({
        items: [{ question: row.key, action, note, based_on: row.answer?.response_id ?? null }],
      });
      setComposer(null);
      setText('');
    } catch (err) {
      fail(err);
    }
  };

  return (
    <li className={`card review-item ${row.state}`}>
      <div className="row-between top">
        <div className="grow">
          <div className="muted small">
            {row.key}
            {row.stage && ` · ${row.stage.name}`}
          </div>
          <div className="qtext">
            {row.text}
            {row.mandatory && <span className="req" title="Required"> *</span>}
          </div>
        </div>
        <span className={`pill ${STATE_TONE[row.state]}`}>{REVIEW_STATE_LABEL[row.state]}</span>
      </div>

      <div className="review-answer">
        {row.answer ? (
          <>
            <b>{def ? summarize(def.definition, row.answer.value) : JSON.stringify(row.answer.value)}</b>
            <span className="muted small"> · {PROVENANCE_LABEL[row.answer.provenance] ?? row.answer.provenance} · {new Date(row.answer.answered_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' })}</span>
          </>
        ) : (
          <span className="muted">Not answered</span>
        )}
      </div>

      {row.failing.length > 0 && (
        <ul className="failing">
          {row.failing.map((f) => (
            <li key={f.control} className={f.kill_switch ? 'bad' : 'warn'}>
              <AlertTriangle size={13} /> {f.control} {f.name}: {f.status === 'missing' ? 'not in place' : 'partly in place'}
              {f.kill_switch && <span className="badge bad">Kill switch</span>}
            </li>
          ))}
        </ul>
      )}

      <ClaimList claims={row.claims} />

      {row.latest_review?.note && row.state !== 'to_review' && (
        <p className="review-note small">
          <Flag size={12} /> {row.latest_review.note}
          <span className="muted"> · {row.latest_review.created_by_name ?? 'Reviewer'}</span>
        </p>
      )}
      {past.map((q) => <RequestNote key={q.id} q={q} />)}
      {live && <RequestNote q={live} />}

      <div className="row gap wrap review-actions">
        {canWrite && (
          <>
          {row.answer && row.state !== 'approved' && !live && (
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => decide('approve')}>
              <Check size={14} /> Approve
            </button>
          )}
          {row.answer && row.state !== 'flagged' && !live && (
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setComposer(composer === 'flag' ? null : 'flag')}>
              <Flag size={14} /> Flag
            </button>
          )}
          {!live && (
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setComposer(composer === 'request' ? null : 'request')}>
              <MessageSquareReply size={14} /> Ask for a change
            </button>
          )}
          {(row.state === 'approved' || row.state === 'flagged') && (
            <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => decide('clear')}>
              <Undo2 size={14} /> Undo
            </button>
          )}
          {live && (
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              disabled={busy}
              onClick={async () => {
                try {
                  await withdraw.mutateAsync(live.id);
                } catch (err) {
                  fail(err);
                }
              }}
            >
              <RotateCcw size={14} /> Withdraw request
            </button>
          )}
          </>
        )}
        <a className="btn btn-sm btn-ghost" href={href.messages(id, row.key)}>
          <MessageSquare size={14} /> Comment
        </a>
      </div>

      {composer && (
        <form
          className="stack-sm review-composer"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!text.trim()) return;
            if (composer === 'flag') return decide('flag', text.trim());
            try {
              await request.mutateAsync({ question: row.key, message: text.trim() });
              setComposer(null);
              setText('');
            } catch (err) {
              fail(err);
            }
          }}
        >
          <label className="small" htmlFor={`compose-${row.key}`}>
            {composer === 'flag' ? 'Why flag it? Only your team sees this.' : 'What should the business change or send? They see this.'}
          </label>
          <textarea
            id={`compose-${row.key}`}
            className="input"
            rows={2}
            maxLength={2000}
            value={text}
            autoFocus
            onChange={(e) => setText(e.target.value)}
            placeholder={composer === 'flag' ? 'e.g. Check the backup claim with the broker' : 'e.g. Your policy schedule shows MFA is only on email. Please correct this answer or upload proof.'}
          />
          <div className="row gap">
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy || !text.trim()}>
              {composer === 'flag' ? 'Flag' : 'Add request'}
            </button>
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => setComposer(null)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </li>
  ); // prettier-ignore
}

const REQUEST_STATE: Record<ChangeRequest['state'], string> = {
  open: 'Not sent yet',
  sent: 'With the business',
  answered: 'Answered',
  withdrawn: 'Withdrawn',
};

function RequestNote({ q }: { q: ChangeRequest }) {
  return (
    <div className={`request-note ${q.state}`}>
      <div className="small">
        <b>{REQUEST_STATE[q.state]}</b>
        <span className="muted"> · asked by {q.created_by_name ?? 'reviewer'}</span>
      </div>
      <p className="small">{q.message}</p>
      {q.reply && (
        <p className="small reply">
          <b>Business replied:</b> {q.reply}
        </p>
      )}
    </div>
  );
}
